/**
 * Intro Video Section
 * 
 * Full-width video player with custom controls (play/pause, speed selector).
 * Two-column layout: short intro text on left, video on right (stacks on mobile).
 * Video does NOT autoplay — user must click to play.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';

const VIDEO_URL = 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/HTmC6kmU7Xh3XwwcFSK4D7/260302-intro-video-v3_3b7ba14a.mov';
const POSTER_URL = 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/HTmC6kmU7Xh3XwwcFSK4D7/Screenshot2026-03-05at11.13.08PM_5f443d94.png';

const SPEED_OPTIONS = [1, 1.2, 1.5] as const;

export default function IntroVideoSection() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [speed, setSpeed] = useState<number>(1);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const hideControlsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setIsPlaying(true);
      setHasStarted(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  }, []);

  const cycleSpeed = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const currentIndex = SPEED_OPTIONS.indexOf(speed as typeof SPEED_OPTIONS[number]);
    const nextIndex = (currentIndex + 1) % SPEED_OPTIONS.length;
    const newSpeed = SPEED_OPTIONS[nextIndex];
    video.playbackRate = newSpeed;
    setSpeed(newSpeed);
  }, [speed]);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    const bar = progressRef.current;
    if (!video || !bar) return;
    const rect = bar.getBoundingClientRect();
    const fraction = (e.clientX - rect.left) / rect.width;
    video.currentTime = fraction * video.duration;
  }, []);

  const handleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      container.requestFullscreen();
    }
  }, []);

  // Auto-hide controls after 3s of no interaction when playing
  const resetControlsTimer = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
    if (isPlaying) {
      hideControlsTimer.current = setTimeout(() => setShowControls(false), 3000);
    }
  }, [isPlaying]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      setProgress(video.duration ? (video.currentTime / video.duration) * 100 : 0);
    };
    const onLoadedMetadata = () => {
      setDuration(video.duration);
    };
    const onEnded = () => {
      setIsPlaying(false);
      setShowControls(true);
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('ended', onEnded);

    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('ended', onEnded);
    };
  }, []);

  useEffect(() => {
    if (!isPlaying) {
      setShowControls(true);
      if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
    }
  }, [isPlaying]);

  return (
    <section className="py-16 sm:py-20 bg-white">
      <div className="container px-4 max-w-6xl mx-auto">
        {/* Two-column layout: text + video */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
          {/* Left: Intro text */}
          <div className="lg:w-[35%] flex-shrink-0">
            <div className="inline-flex items-center gap-2 bg-[#6a63f5]/5 border border-[#6a63f5]/10 rounded-full px-4 py-1.5 mb-5">
              <Play className="w-3.5 h-3.5 text-[#6a63f5]" fill="currentColor" />
              <span className="text-xs font-medium text-[#6a63f5]/70 uppercase tracking-wider">Watch in 2 minutes</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 tracking-tight text-gray-900">
              Meet Ryder One
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-4">
              See how Ryder One makes self-custody simple — from 60-second setup to tap-to-recover. No seed phrases, no complexity, no stress.
            </p>
            <p className="text-sm text-gray-400">
              1 min 47 sec &middot; Everything you need to know
            </p>
          </div>

          {/* Right: Video player */}
          <div className="lg:w-[65%] w-full">
            <div
              ref={containerRef}
              className="relative rounded-2xl overflow-hidden bg-black group cursor-pointer shadow-2xl shadow-black/10"
              onMouseMove={resetControlsTimer}
              onMouseEnter={resetControlsTimer}
            >
                <video
                ref={videoRef}
                src={VIDEO_URL}
                poster={POSTER_URL}
                className="w-full aspect-video object-cover"
                playsInline
                preload="metadata"
                onClick={togglePlay}
              />

              {/* Big play button overlay (before first play) */}
              {!hasStarted && (
                <div
                  className="absolute inset-0 flex items-center justify-center bg-black/30 z-10"
                  onClick={togglePlay}
                >
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-2xl hover:scale-110 transition-transform duration-300">
                    <Play className="w-8 h-8 sm:w-10 sm:h-10 text-[#6a63f5] ml-1" fill="currentColor" />
                  </div>
                </div>
              )}

              {/* Controls bar */}
              <div
                className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-12 pb-3 px-4 transition-opacity duration-300 z-20 ${
                  showControls || !isPlaying ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
              >
                {/* Progress bar */}
                <div
                  ref={progressRef}
                  className="w-full h-1.5 bg-white/20 rounded-full mb-3 cursor-pointer group/progress hover:h-2.5 transition-all"
                  onClick={handleProgressClick}
                >
                  <div
                    className="h-full bg-[#6a63f5] rounded-full relative transition-all"
                    style={{ width: `${progress}%` }}
                  >
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md opacity-0 group-hover/progress:opacity-100 transition-opacity" />
                  </div>
                </div>

                {/* Controls row */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {/* Play/Pause */}
                    <button
                      onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                      className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    >
                      {isPlaying ? (
                        <Pause className="w-4 h-4 text-white" fill="currentColor" />
                      ) : (
                        <Play className="w-4 h-4 text-white ml-0.5" fill="currentColor" />
                      )}
                    </button>

                    {/* Mute */}
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                      className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    >
                      {isMuted ? (
                        <VolumeX className="w-4 h-4 text-white" />
                      ) : (
                        <Volume2 className="w-4 h-4 text-white" />
                      )}
                    </button>

                    {/* Time */}
                    <span className="text-xs text-white/70 font-mono tabular-nums">
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Speed selector */}
                    <button
                      onClick={(e) => { e.stopPropagation(); cycleSpeed(); }}
                      className="h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    >
                      <span className="text-xs font-semibold text-white tabular-nums">
                        {speed}x
                      </span>
                    </button>

                    {/* Fullscreen */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleFullscreen(); }}
                      className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    >
                      <Maximize className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
